import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, X, User, MapPin, Calendar, Truck, Clock, 
  ChevronRight, Briefcase, Mail, LogIn, Package, Home, 
  Users, Send, CheckCircle, ArrowRight, Shield, Zap,
  Building2, UserCheck, Box, Timer, Award, Target,
  ChevronLeft, Leaf
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Logo DLV Express
const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";

// 3D Illustrations
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/7057fa00-c3b8-49c5-a880-d1380f605217/images/156eb388bd58111017b9cce607b18970570d258669ad3db977b888bb389f82f1.png";
const ABOUT_COMPANY = "https://static.prod-images.emergentagent.com/jobs/96b1639d-f40b-4035-a8da-79280d593c62/images/725214a260817a6ad46de0fdd6ff588f7fae95ce51bb161517f747321e7918e7.png";
const RECRUITMENT_TEAM = "https://static.prod-images.emergentagent.com/jobs/7057fa00-c3b8-49c5-a880-d1380f605217/images/8e2e5f9eea9d6ffb786b5972bff8676332e8781af3a4126feaf22939b1776503.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showTransportParticulierModal, setShowTransportParticulierModal] = useState(false);
  const [showTransportEntrepriseModal, setShowTransportEntrepriseModal] = useState(false);
  const [showMovingModal, setShowMovingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [expandedService, setExpandedService] = useState(null); // 'transport' or 'demenagement'

  // Application form data
  const [applyData, setApplyData] = useState({
    last_name: '', first_name: '', address: '', postal_code: '',
    email: '', phone: '', birth_date: '', experience: '',
    license_years: '', has_vehicle: false, vehicle_type: '', 
    job_type: 'chauffeur', disponibilite: '', capacite_physique: ''
  });

  // Transport request form data
  const [transportData, setTransportData] = useState({
    name: '', phone: '', email: '', address_from: '',
    address_to: '', merchandise_type: '', volume: '', date: '', message: '',
    client_type: 'particulier'
  });

  // Moving request form data
  const [requestData, setRequestData] = useState({
    name: '', phone: '', email: '', address_from: '',
    address_to: '', volume: '', date: '', message: '', request_type: 'demenagement'
  });

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = ['services', 'zones', 'why-us', 'recruitment', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Form handlers
  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    if (!applyData.last_name || !applyData.first_name || !applyData.email || !applyData.phone) {
      setError('Veuillez remplir tous les champs obligatoires');
      setLoading(false);
      return;
    }
    try {
      await axios.post(`${API}/candidatures`, applyData);
      setSuccess('Votre candidature a été envoyée avec succès !');
      setApplyData({ last_name: '', first_name: '', address: '', postal_code: '', email: '', phone: '', birth_date: '', experience: '', license_years: '', has_vehicle: false, vehicle_type: '', job_type: 'chauffeur', disponibilite: '', capacite_physique: '' });
      setTimeout(() => { setShowApplyModal(false); setSuccess(''); }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleTransportSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    if (!transportData.name || !transportData.phone || !transportData.email || !transportData.address_from || !transportData.address_to) {
      setError('Veuillez remplir tous les champs obligatoires');
      setLoading(false);
      return;
    }
    try {
      await axios.post(`${API}/demandes-transport-b2c`, transportData);
      setSuccess('Votre demande a été envoyée avec succès !');
      setTransportData({ name: '', phone: '', email: '', address_from: '', address_to: '', merchandise_type: '', volume: '', date: '', message: '', client_type: 'particulier' });
      setTimeout(() => { setShowTransportParticulierModal(false); setShowTransportEntrepriseModal(false); setSuccess(''); }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    if (!requestData.name || !requestData.phone || !requestData.email || !requestData.address_from || !requestData.address_to) {
      setError('Veuillez remplir tous les champs obligatoires');
      setLoading(false);
      return;
    }
    try {
      await axios.post(`${API}/demandes-client`, requestData);
      setSuccess('Votre demande a été envoyée avec succès !');
      setRequestData({ name: '', phone: '', email: '', address_from: '', address_to: '', volume: '', date: '', message: '', request_type: 'demenagement' });
      setTimeout(() => { setShowMovingModal(false); setSuccess(''); }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ==================== HEADER ==================== */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-[#0a1628]/95 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <div className="flex items-center">
              <img src={LOGO_URL} alt="DLV Express" className="h-10 lg:h-12 w-auto" />
            </div>

            <nav className="hidden lg:flex items-center space-x-8">
              {[
                { label: 'Accueil', section: 'hero' },
                { label: 'Services', section: 'services' },
                { label: 'Zones', section: 'zones' },
                { label: 'Pourquoi nous', section: 'why-us' },
                { label: 'Recrutement', section: 'recruitment' },
                { label: 'Contact', section: 'contact' }
              ].map((item) => (
                <button 
                  key={item.section}
                  onClick={() => scrollToSection(item.section)}
                  className={`font-medium transition-colors ${activeSection === item.section ? 'text-blue-500' : scrolled ? 'text-slate-700 hover:text-blue-600' : 'text-white hover:text-blue-300'}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')} className={`border-2 ${scrolled ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : 'border-white/30 text-white hover:bg-white/10'}`}>
                <LogIn className="w-4 h-4 mr-2" />
                Espace salarié
              </Button>
              <Button onClick={() => setShowApplyModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg">
                <Briefcase className="w-4 h-4 mr-2" />
                Postuler
              </Button>
            </div>

            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className={`w-6 h-6 ${scrolled ? 'text-slate-900' : 'text-white'}`} /> : <Menu className={`w-6 h-6 ${scrolled ? 'text-slate-900' : 'text-white'}`} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t shadow-xl">
            <div className="px-4 py-4 space-y-3">
              {['Accueil', 'Services', 'Zones', 'Pourquoi nous', 'Recrutement', 'Contact'].map((item, i) => (
                <button key={i} onClick={() => scrollToSection(['hero', 'services', 'zones', 'why-us', 'recruitment', 'contact'][i])} className="block w-full text-left py-2 text-slate-700 font-medium">{item}</button>
              ))}
              <div className="pt-4 space-y-2">
                <Button variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="w-full">
                  <LogIn className="w-4 h-4 mr-2" /> Espace salarié
                </Button>
                <Button onClick={() => { setShowApplyModal(true); setMobileMenuOpen(false); }} className="w-full bg-blue-600">
                  <Briefcase className="w-4 h-4 mr-2" /> Postuler
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ==================== HERO SECTION ==================== */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1a365d] to-[#0a1628]"></div>
        
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>

        {/* Decorative shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                <span className="text-blue-400">DLV Express</span> – Votre partenaire de confiance
              </h1>
              <p className="text-lg lg:text-xl text-white/80 mb-8 max-w-xl">
                Transport, livraison de colis et déménagement pour particuliers et entreprises. Une équipe professionnelle à votre service partout en France.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" onClick={() => scrollToSection('services')} className="bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-600/30 px-8">
                  <Package className="w-5 h-5 mr-2" />
                  Nos services
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/devis')} className="border-2 border-white/30 text-white hover:bg-white/10 px-8">
                  Demander un devis
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <img src={HERO_IMAGE} alt="DLV Express - Transport et Livraison" className="w-full max-w-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SERVICES SECTION ==================== */}
      <section id="services" className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white to-slate-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Nos Services
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Des solutions adaptées à tous vos besoins
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Transport, livraison et déménagement : DLV Express vous accompagne dans tous vos projets logistiques.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service 1: Transport Particuliers */}
            <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100">
              <div className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/96b1639d-f40b-4035-a8da-79280d593c62/images/577f64bd75015e565698e1ca6d8a66cab1e49839adf52defc8be56d62f8be27f.png" 
                  alt="Transport Particuliers" 
                  className="w-40 h-40 object-contain group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Transport & Livraison Particuliers</h3>
                <p className="text-slate-600 mb-5 text-sm">
                  Livraison de colis, transport de marchandises et messagerie express pour les particuliers dans toute la France.
                </p>
                <ul className="space-y-2 mb-6">
                  {['Livraison à domicile', 'Transport de colis', 'Service rapide'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => { setTransportData({...transportData, client_type: 'particulier'}); setShowTransportParticulierModal(true); }} className="w-full bg-blue-600 hover:bg-blue-700">
                  Demander un devis
                </Button>
              </div>
            </div>

            {/* Service 2: Transport Entreprises */}
            <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100">
              <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/96b1639d-f40b-4035-a8da-79280d593c62/images/c9079cbeddb8d5270343498f651ce3afc9af12a55117bd8ed79faff848b85ea4.png" 
                  alt="Transport Entreprises" 
                  className="w-40 h-40 object-contain group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Transport & Livraison Entreprises</h3>
                <p className="text-slate-600 mb-5 text-sm">
                  Solutions logistiques sur mesure pour les professionnels : transport B2B, gestion de flux et livraison express.
                </p>
                <ul className="space-y-2 mb-6">
                  {['Contrats réguliers', 'Livraison B2B', 'Suivi personnalisé'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => { setTransportData({...transportData, client_type: 'entreprise'}); setShowTransportEntrepriseModal(true); }} className="w-full bg-slate-800 hover:bg-slate-900">
                  Demander un devis
                </Button>
              </div>
            </div>

            {/* Service 3: Déménagement */}
            <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100">
              <div className="h-48 bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/96b1639d-f40b-4035-a8da-79280d593c62/images/9e3279a82c408d38593040c648fdac5131e73e9205bb01d7572d047f5aabbc90.png" 
                  alt="Déménagement" 
                  className="w-40 h-40 object-contain group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Déménagement</h3>
                <p className="text-slate-600 mb-5 text-sm">
                  Service de déménagement complet pour particuliers et professionnels en Île-de-France et régions voisines.
                </p>
                <ul className="space-y-2 mb-6">
                  {['Équipes professionnelles', 'Matériel de protection', 'Devis gratuit'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => setShowMovingModal(true)} className="w-full bg-orange-500 hover:bg-orange-600">
                  Demander un devis
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ZONES D'INTERVENTION ==================== */}
      <section id="zones" className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1a365d] to-[#0a1628]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-sm font-medium mb-4 border border-white/20">
              Zones d'intervention
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Nous intervenons partout en France
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Transport et livraison sur toute la France, déménagement en Île-de-France et Bretagne.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Illustration 3D logistique */}
            <div className="relative flex justify-center">
              <div className="relative w-full max-w-lg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/a1406132-a684-48c9-924d-9e1842969519/images/665c82b1eb642edcdb5824c7d61b4318a26fb2941ed4332b229065d867ed8490.png" 
                  alt="Réseau de livraison DLV Express" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                  style={{filter: 'drop-shadow(0 20px 40px rgba(59, 130, 246, 0.3))'}}
                />
              </div>
            </div>

            {/* Légende simple */}
            <div className="space-y-4">
              {/* Transport & Livraison */}
              <div 
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all cursor-pointer"
                onClick={() => setExpandedService(expandedService === 'transport' ? null : 'transport')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Truck className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">Transport & Livraison</h3>
                    <p className="text-blue-300 text-sm font-medium">Toute la France (sur devis)</p>
                  </div>
                  <ChevronRight className={`w-6 h-6 text-white/70 transition-transform duration-300 ${expandedService === 'transport' ? 'rotate-90' : ''}`} />
                </div>
                {/* Description extensible */}
                <div className={`overflow-hidden transition-all duration-300 ${expandedService === 'transport' ? 'max-h-40 mt-4 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-white/80 text-sm leading-relaxed">
                    Nous proposons des solutions de transport et de livraison de colis et marchandises sur l'ensemble du territoire français. Chaque demande est étudiée afin de proposer une solution adaptée et un devis personnalisé selon la distance, le volume et les besoins spécifiques.
                  </p>
                </div>
              </div>

              {/* Déménagement */}
              <div 
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all cursor-pointer"
                onClick={() => setExpandedService(expandedService === 'demenagement' ? null : 'demenagement')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                    <Home className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">Déménagement</h3>
                    <p className="text-orange-300 text-sm font-medium">Île-de-France, régions voisines et Bretagne</p>
                  </div>
                  <ChevronRight className={`w-6 h-6 text-white/70 transition-transform duration-300 ${expandedService === 'demenagement' ? 'rotate-90' : ''}`} />
                </div>
                {/* Description extensible */}
                <div className={`overflow-hidden transition-all duration-300 ${expandedService === 'demenagement' ? 'max-h-40 mt-4 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-white/80 text-sm leading-relaxed">
                    Nous accompagnons les particuliers pour leurs déménagements en Île-de-France, en Bretagne et dans les régions voisines. Chaque projet est étudié afin de proposer une organisation adaptée et un devis personnalisé en fonction du volume, de la distance et des contraintes du logement.
                  </p>
                </div>
              </div>

              {/* CTA - Redirige vers la section Services */}
              <Button 
                onClick={() => scrollToSection('services')} 
                size="lg" 
                className="w-full bg-white text-slate-900 hover:bg-slate-100 shadow-xl mt-4"
              >
                Demander un devis gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== WHY CHOOSE US ==================== */}
      <section id="why-us" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Nos engagements
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi choisir DLV Express ?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Rapidité */}
            <div className="text-center p-6 rounded-2xl hover:bg-slate-50 transition-colors">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Timer className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Rapidité</h3>
              <p className="text-slate-600 text-sm">Livraison fiable et respect des délais convenus</p>
            </div>

            {/* Sécurité */}
            <div className="text-center p-6 rounded-2xl hover:bg-slate-50 transition-colors">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Sécurité</h3>
              <p className="text-slate-600 text-sm">Transport et manipulation soignés de vos biens</p>
            </div>

            {/* Flexibilité */}
            <div className="text-center p-6 rounded-2xl hover:bg-slate-50 transition-colors">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Flexibilité</h3>
              <p className="text-slate-600 text-sm">Solutions adaptées aux particuliers et entreprises</p>
            </div>

            {/* Professionnalisme */}
            <div className="text-center p-6 rounded-2xl hover:bg-slate-50 transition-colors">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-slate-700" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Professionnalisme</h3>
              <p className="text-slate-600 text-sm">Chauffeurs expérimentés et formés</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ABOUT SECTION ==================== */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={ABOUT_COMPANY} alt="DLV Express - Notre entreprise" className="w-full rounded-2xl shadow-xl" />
            </div>
            <div>
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                À propos
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                DLV Express, votre partenaire logistique
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                DLV Express est une entreprise spécialisée dans le transport et la logistique du dernier kilomètre. Grâce à une organisation optimisée et une équipe expérimentée, nous assurons des livraisons fiables et professionnelles.
              </p>
              <p className="text-slate-600 mb-8">
                Notre mission est d'offrir un service de qualité pour les entreprises comme pour les particuliers. La satisfaction de nos clients est au cœur de nos préoccupations.
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">19 000+</div>
                  <div className="text-slate-500 text-sm">Colis / semaine</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">98%</div>
                  <div className="text-slate-500 text-sm">Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">24/7</div>
                  <div className="text-slate-500 text-sm">Disponibilité</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ENGAGEMENT ENVIRONNEMENTAL ==================== */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Background image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/a1406132-a684-48c9-924d-9e1842969519/images/4d96c606981d6331f89b94ac366e61ecadd08099776da162617fce93fd2a6521.png')`,
          }}
        />
        {/* Soft overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/40" />
        
        {/* Decorative SVG leaves */}
        <svg className="absolute top-0 left-0 w-64 h-64 text-emerald-200/30 -translate-x-1/3 -translate-y-1/3" viewBox="0 0 200 200" fill="currentColor">
          <path d="M100,10 Q140,50 130,100 Q120,150 80,180 Q40,150 30,100 Q20,50 60,20 Q80,10 100,10 Z" />
        </svg>
        <svg className="absolute top-20 right-10 w-32 h-32 text-teal-200/40 rotate-45" viewBox="0 0 100 100" fill="currentColor">
          <ellipse cx="50" cy="50" rx="20" ry="45" />
        </svg>
        <svg className="absolute bottom-10 left-20 w-40 h-40 text-emerald-300/30 rotate-12" viewBox="0 0 100 100" fill="currentColor">
          <path d="M50,5 Q75,25 70,50 Q65,75 50,90 Q35,75 30,50 Q25,25 50,5 Z" />
        </svg>
        <svg className="absolute bottom-20 right-1/4 w-24 h-24 text-teal-300/25 -rotate-30" viewBox="0 0 100 100" fill="currentColor">
          <ellipse cx="50" cy="50" rx="15" ry="40" />
        </svg>
        <svg className="absolute top-1/3 right-0 w-48 h-48 text-emerald-200/20 translate-x-1/3" viewBox="0 0 200 200" fill="currentColor">
          <path d="M100,20 Q150,60 140,110 Q130,160 90,180 Q50,160 40,110 Q30,60 80,30 Q90,20 100,20 Z" />
        </svg>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white/80 backdrop-blur-sm text-emerald-700 rounded-full text-sm font-medium mb-4 shadow-sm border border-emerald-100">
              Notre engagement
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Engagés pour l'environnement
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto">
              DLV Express s'engage dans une démarche éco-responsable pour réduire son impact environnemental et contribuer à un avenir plus durable.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Optimisation des trajets */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-emerald-100/50 hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                <MapPin className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Optimisation des trajets</h3>
              <p className="text-slate-600">
                Nous optimisons nos itinéraires afin de réduire les distances parcourues et limiter l'impact environnemental de nos activités de transport.
              </p>
            </div>

            {/* Conduite responsable */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-emerald-100/50 hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                <Leaf className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Conduite responsable</h3>
              <p className="text-slate-600">
                Nos chauffeurs sont sensibilisés aux bonnes pratiques de conduite afin de favoriser une utilisation plus raisonnée des ressources et une logistique plus durable.
              </p>
            </div>

            {/* Logistique responsable */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-emerald-100/50 hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                <Truck className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Logistique responsable</h3>
              <p className="text-slate-600">
                Nous mettons en place des solutions de transport plus efficaces et réfléchies afin de réduire notre empreinte environnementale et contribuer à une logistique plus respectueuse.
              </p>
            </div>
          </div>

          {/* Statistiques environnementales */}
          <div className="mt-16 bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-emerald-100/50">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="p-4">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 mb-2">–15%</div>
                <p className="text-slate-600">Réduction de l'empreinte carbone grâce à l'optimisation des trajets</p>
              </div>
              <div className="p-4 border-x border-emerald-100">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 mb-2">100%</div>
                <p className="text-slate-600">Chauffeurs sensibilisés à une conduite plus responsable</p>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 mb-2">Transport durable</div>
                <p className="text-slate-600">Une approche logistique pensée pour limiter l'impact environnemental de nos opérations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== RECRUITMENT SECTION ==================== */}
      <section id="recruitment" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img src={RECRUITMENT_TEAM} alt="Rejoignez notre équipe" className="w-full rounded-2xl shadow-xl" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                Recrutement
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Rejoignez l'équipe DLV Express
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Nous recherchons des talents motivés pour rejoindre notre équipe dynamique.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { title: 'Chauffeur / Livreur', contracts: 'CDI • CDD • Alternance' },
                  { title: 'Déménageur', contracts: 'CDI • CDD • Alternance' }
                ].map((job, i) => (
                  <div key={i} className="flex items-center gap-4 bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition-colors">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      {i === 0 ? <Truck className="w-6 h-6 text-blue-600" /> : <Box className="w-6 h-6 text-blue-600" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{job.title}</h4>
                      <p className="text-sm text-slate-500">{job.contracts}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                ))}
              </div>

              <Button onClick={() => setShowApplyModal(true)} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg">
                <Send className="w-5 h-5 mr-2" />
                Postuler maintenant
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CONTACT SECTION ==================== */}
      <section id="contact" className="py-24 lg:py-32 bg-gradient-to-br from-[#0a1628] via-[#1a365d] to-[#0a1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-sm font-medium mb-4 border border-white/20">
              Contact
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Contactez-nous
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Une question ? Un projet ? Notre équipe est à votre disposition.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <a href="mailto:contact@dlv-express.com" className="group bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10 hover:bg-white/20 transition-all">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email</h3>
              <p className="text-white/70">contact@dlv-express.com</p>
            </a>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Adresse</h3>
              <p className="text-white/70">41 rue d'Aulnay<br />95500 Gonesse</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-[#0a1628] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="DLV Express" className="h-12 w-auto mb-4" />
              <p className="text-white/60 mb-4 max-w-md">
                DLV Express – Votre partenaire de confiance pour le transport, la livraison de colis et le déménagement.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2">
                {['Transport Particuliers', 'Transport Entreprises', 'Déménagement'].map((item, i) => (
                  <li key={i}>
                    <button onClick={() => scrollToSection('services')} className="text-white/60 hover:text-white transition-colors">{item}</button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Liens rapides</h4>
              <ul className="space-y-2">
                {[
                  { label: 'Zones d\'intervention', section: 'zones' },
                  { label: 'Recrutement', section: 'recruitment' },
                  { label: 'Contact', section: 'contact' },
                  { label: 'Espace salarié', action: () => navigate('/login') }
                ].map((item, i) => (
                  <li key={i}>
                    <button onClick={item.action || (() => scrollToSection(item.section))} className="text-white/60 hover:text-white transition-colors">{item.label}</button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">© 2026 DLV Express - Tous droits réservés</p>
            <p className="text-white/40 text-sm">41 rue d'Aulnay, 95500 Gonesse</p>
          </div>
        </div>
      </footer>

      {/* ==================== MODALS ==================== */}
      
      {/* Transport Particulier Modal */}
      {showTransportParticulierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Transport Particulier</h3>
                    <p className="text-sm text-slate-500">Demandez votre devis gratuit</p>
                  </div>
                </div>
                <button onClick={() => setShowTransportParticulierModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <form onSubmit={handleTransportSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
              {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">{success}</div>}
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nom complet *</Label><Input value={transportData.name} onChange={(e) => setTransportData({...transportData, name: e.target.value})} placeholder="Votre nom" /></div>
                <div><Label>Téléphone *</Label><Input value={transportData.phone} onChange={(e) => setTransportData({...transportData, phone: e.target.value})} placeholder="06 00 00 00 00" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" value={transportData.email} onChange={(e) => setTransportData({...transportData, email: e.target.value})} placeholder="votre@email.com" /></div>
              <div><Label>Adresse de départ *</Label><Input value={transportData.address_from} onChange={(e) => setTransportData({...transportData, address_from: e.target.value})} placeholder="Adresse complète" /></div>
              <div><Label>Adresse d'arrivée *</Label><Input value={transportData.address_to} onChange={(e) => setTransportData({...transportData, address_to: e.target.value})} placeholder="Adresse complète" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type de marchandise</Label><Input value={transportData.merchandise_type} onChange={(e) => setTransportData({...transportData, merchandise_type: e.target.value})} placeholder="Ex: Colis, Meubles..." /></div>
                <div><Label>Date souhaitée</Label><Input type="date" value={transportData.date} onChange={(e) => setTransportData({...transportData, date: e.target.value})} /></div>
              </div>
              <div><Label>Message</Label><Textarea value={transportData.message} onChange={(e) => setTransportData({...transportData, message: e.target.value})} placeholder="Informations complémentaires..." rows={3} /></div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">{loading ? 'Envoi...' : 'Envoyer ma demande'}</Button>
            </form>
          </div>
        </div>
      )}

      {/* Transport Entreprise Modal */}
      {showTransportEntrepriseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Transport Entreprise</h3>
                    <p className="text-sm text-slate-500">Solution B2B sur mesure</p>
                  </div>
                </div>
                <button onClick={() => setShowTransportEntrepriseModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <form onSubmit={handleTransportSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
              {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">{success}</div>}
              
              <div><Label>Nom de l'entreprise *</Label><Input value={transportData.name} onChange={(e) => setTransportData({...transportData, name: e.target.value})} placeholder="Nom de votre société" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Téléphone *</Label><Input value={transportData.phone} onChange={(e) => setTransportData({...transportData, phone: e.target.value})} placeholder="01 00 00 00 00" /></div>
                <div><Label>Email *</Label><Input type="email" value={transportData.email} onChange={(e) => setTransportData({...transportData, email: e.target.value})} placeholder="contact@entreprise.com" /></div>
              </div>
              <div><Label>Adresse de départ *</Label><Input value={transportData.address_from} onChange={(e) => setTransportData({...transportData, address_from: e.target.value})} placeholder="Adresse complète" /></div>
              <div><Label>Adresse d'arrivée *</Label><Input value={transportData.address_to} onChange={(e) => setTransportData({...transportData, address_to: e.target.value})} placeholder="Adresse complète" /></div>
              <div><Label>Type de marchandise</Label><Input value={transportData.merchandise_type} onChange={(e) => setTransportData({...transportData, merchandise_type: e.target.value})} placeholder="Ex: Palettes, colis..." /></div>
              <div><Label>Décrivez votre besoin</Label><Textarea value={transportData.message} onChange={(e) => setTransportData({...transportData, message: e.target.value})} placeholder="Fréquence, volumes, contraintes..." rows={4} /></div>
              <Button type="submit" disabled={loading} className="w-full bg-slate-800 hover:bg-slate-900">{loading ? 'Envoi...' : 'Demander un devis'}</Button>
            </form>
          </div>
        </div>
      )}

      {/* Moving Modal */}
      {showMovingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Déménagement</h3>
                    <p className="text-sm text-slate-500">Demandez votre devis gratuit</p>
                  </div>
                </div>
                <button onClick={() => setShowMovingModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <form onSubmit={handleRequestSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
              {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">{success}</div>}
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nom complet *</Label><Input value={requestData.name} onChange={(e) => setRequestData({...requestData, name: e.target.value})} placeholder="Votre nom" /></div>
                <div><Label>Téléphone *</Label><Input value={requestData.phone} onChange={(e) => setRequestData({...requestData, phone: e.target.value})} placeholder="06 00 00 00 00" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" value={requestData.email} onChange={(e) => setRequestData({...requestData, email: e.target.value})} placeholder="votre@email.com" /></div>
              <div><Label>Adresse de départ *</Label><Input value={requestData.address_from} onChange={(e) => setRequestData({...requestData, address_from: e.target.value})} placeholder="Adresse complète" /></div>
              <div><Label>Adresse d'arrivée *</Label><Input value={requestData.address_to} onChange={(e) => setRequestData({...requestData, address_to: e.target.value})} placeholder="Adresse complète" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Volume estimé</Label><Input value={requestData.volume} onChange={(e) => setRequestData({...requestData, volume: e.target.value})} placeholder="Ex: 30m³" /></div>
                <div><Label>Date souhaitée</Label><Input type="date" value={requestData.date} onChange={(e) => setRequestData({...requestData, date: e.target.value})} /></div>
              </div>
              <div><Label>Message</Label><Textarea value={requestData.message} onChange={(e) => setRequestData({...requestData, message: e.target.value})} placeholder="Type de logement, étage, ascenseur..." rows={3} /></div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600">{loading ? 'Envoi...' : 'Envoyer ma demande'}</Button>
            </form>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Rejoignez-nous</h3>
                    <p className="text-sm text-slate-500">Postulez chez DLV Express</p>
                  </div>
                </div>
                <button onClick={() => setShowApplyModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <form onSubmit={handleApplySubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
              {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">{success}</div>}
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nom *</Label><Input value={applyData.last_name} onChange={(e) => setApplyData({...applyData, last_name: e.target.value})} placeholder="Votre nom" /></div>
                <div><Label>Prénom *</Label><Input value={applyData.first_name} onChange={(e) => setApplyData({...applyData, first_name: e.target.value})} placeholder="Votre prénom" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" value={applyData.email} onChange={(e) => setApplyData({...applyData, email: e.target.value})} placeholder="votre@email.com" /></div>
              <div><Label>Téléphone *</Label><Input value={applyData.phone} onChange={(e) => setApplyData({...applyData, phone: e.target.value})} placeholder="06 00 00 00 00" /></div>
              
              <div>
                <Label>Poste souhaité *</Label>
                <select value={applyData.job_type} onChange={(e) => setApplyData({...applyData, job_type: e.target.value})} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="chauffeur">Chauffeur / Livreur</option>
                  <option value="demenageur">Déménageur</option>
                </select>
              </div>

              <div>
                <Label>Type de contrat</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="cdi">CDI</option>
                  <option value="cdd">CDD</option>
                  <option value="alternance">Alternance</option>
                </select>
              </div>

              {/* Conditional fields based on job type */}
              {applyData.job_type === 'chauffeur' && (
                <>
                  <div><Label>Années de permis</Label><Input value={applyData.license_years} onChange={(e) => setApplyData({...applyData, license_years: e.target.value})} placeholder="Ex: 3 ans" /></div>
                  <div><Label>Expérience livraison</Label><Textarea value={applyData.experience} onChange={(e) => setApplyData({...applyData, experience: e.target.value})} placeholder="Décrivez votre expérience en livraison..." rows={3} /></div>
                </>
              )}

              {applyData.job_type === 'demenageur' && (
                <>
                  <div><Label>Expérience déménagement</Label><Textarea value={applyData.experience} onChange={(e) => setApplyData({...applyData, experience: e.target.value})} placeholder="Décrivez votre expérience en déménagement..." rows={3} /></div>
                  <div><Label>Capacité physique</Label><Textarea value={applyData.capacite_physique} onChange={(e) => setApplyData({...applyData, capacite_physique: e.target.value})} placeholder="Décrivez votre condition physique..." rows={2} /></div>
                </>
              )}

              <div><Label>Disponibilité</Label><Input value={applyData.disponibilite} onChange={(e) => setApplyData({...applyData, disponibilite: e.target.value})} placeholder="Ex: Immédiate, sous 2 semaines..." /></div>

              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">{loading ? 'Envoi...' : 'Envoyer ma candidature'}</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
